import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActionsSubject, Store } from '@ngrx/store';
import { ofType } from '@ngrx/effects';
import { takeUntil } from 'rxjs/operators';
import { PtCoreActions, PtCoreSelectors } from '@pt/pt-core';
import { CovidAssessmentActions, ValidatorGenerator } from '@pt/pt-shared';
import { NavigationData, PtUiAppRoutes, REQUIRED_VALIDATOR_KEY } from '@pt/pt-models';
import { DuplicateCovidAssessmentPayload } from '../../../models';
import { CovidAssessmentsService, FolderService } from '@pt/api-client';
import { CovidAssessmentDocCreateRequest, Folder } from '@primatoo-ms-organization';
import { Organization } from 'it/primatoo/openapi/api/org/schemas/organization/Organization';

@Component({
  selector: 'pt-create-covid-assessment-dialog',
  templateUrl: './create-covid-assessment-dialog.component.html',
  styles:[`
    .loader-container {
      height: 175px;
      display: grid;
      place-items: center;
    }
  `]
})
export class CreateCovidAssessmentDialogComponent implements OnInit, OnDestroy {

  @Input() openOnSuccess = true;
  @Input() dialogMode: 'CREATE' | 'DUPLICATE' = 'CREATE';
  @Input() duplicateCovidAssessmentPayload: DuplicateCovidAssessmentPayload;
  @Input() selectedFolderId: number;

  public org$: Observable<Organization> = this.store.select(
    PtCoreSelectors.organization
  );
  private editInternalIdEnable = false;

  public folders: NavigationData[] = [];
  public selectedFolder: NavigationData;

  public formGroup: FormGroup;
  public REQUIRED = ValidatorGenerator.generateWidgetValidator(REQUIRED_VALIDATOR_KEY);

  private fields: string[] = [];
  public enabledTempate = false;
  public loading = false;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public store: Store,
    public activeModal: NgbActiveModal,
    public actionsSubject: ActionsSubject,
    private folderService: FolderService,
    private covidAssessmentsService: CovidAssessmentsService
  ) {
  }

  setLoading(value: boolean, withDelay = false) {
    if (!withDelay) {
      this.loading = value;
    } else {
      setTimeout(() => {
        this.loading = value;
      }, 300);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.setLoading(true);

    this.org$.pipe(takeUntil(this.destroy$)).subscribe((org) => {
      this.editInternalIdEnable = true; // org.editInternalIdEnable;
      this.alignDisabled();
    });

    this.initNavigationData();

    this.actionsSubject.pipe(
      ofType(
        CovidAssessmentActions.createCovidAssessmentSuccess,
      ),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      if (this.openOnSuccess) {
        this.store.dispatch(PtCoreActions.navigateTo({
          route: [
            PtUiAppRoutes.BUSINESS_MANAGEMENT,
            PtUiAppRoutes.COVID_ASSESSMENTS,
            PtUiAppRoutes.EDIT,
            action?.id.toString()
          ]
        }));
      }
      this.activeModal.close(true);

    });
    this.actionsSubject.pipe(
      ofType(
        CovidAssessmentActions.duplicateCovidAssessmentSuccess,
      ),
      takeUntil(this.destroy$)
    ).subscribe((action) => {

      if (this.openOnSuccess) {
        this.store.dispatch(PtCoreActions.navigateTo({
          route: [
            PtUiAppRoutes.BUSINESS_MANAGEMENT,
            PtUiAppRoutes.COVID_ASSESSMENTS,
            PtUiAppRoutes.EDIT,
            action.response?.id.toString()
          ]
        }));
      }
      this.activeModal.close(true);
    });
  }

  private initNavigationData() {
    this.folderService.findAllFolders().pipe(
      takeUntil(this.destroy$)
    ).subscribe((response: Folder[]) => {
      if (response) {
        this.folders = response
          .map(f => NavigationData.folderToNavigationData(f));
      }

      this.initFormGroup();
    });
  }

  private initFormGroup() {
    switch (this.dialogMode) {
      case 'CREATE':
        if (this.selectedFolderId !== null && this.selectedFolderId !== undefined && this.folders) {
          this.selectedFolder = this.folders?.find((f) => f.id == this.selectedFolderId);
        }

        this.formGroup = new FormGroup({
          name: new FormControl(null, [Validators.required, Validators.maxLength(128)]),
          description: new FormControl(null),
          internalId: new FormControl(null),
          parentId: new FormControl(this.selectedFolder?.name)
        });
        this.alignDisabled();
        break;

      case 'DUPLICATE':
        this.selectedFolder = this.folders?.find((f) => f.id == this.duplicateCovidAssessmentPayload?.payload?.parentId);

        this.covidAssessmentsService.getToOverride().pipe(
          takeUntil(this.destroy$)
        ).subscribe((response) => {
          this.fields = Object.keys(response).filter(r => response[r] === true);

          this.formGroup = new FormGroup({
            name: new FormControl(null, [Validators.required]),
            description: new FormControl(this.duplicateCovidAssessmentPayload?.payload?.description),
            internalId: new FormControl(null),
            parentId: new FormControl(this.selectedFolder?.name)
          });
          this.alignDisabled();
        });
        break;
    }
    this.setLoading(false, true);
  }

  private alignDisabled() {
    if (this.formGroup) {
      if (this.editInternalIdEnable) {
        this.formGroup.controls.internalId.enable();
      } else {
        this.formGroup.controls.internalId.disable()
      }
    }
  }
  checkUsedField(field: string): boolean {
    switch (this.dialogMode) {
      case 'CREATE':
        return true;
      case 'DUPLICATE':
        return this.fields?.includes(field);
    }
    return true;
  }

  confirm() {
    const request = {
      name: this.formGroup.controls.name.value,
      description: this.formGroup.controls.description.value,
      internalId: this.formGroup.controls.internalId.value,
      parentId: this.selectedFolder?.id
    } as CovidAssessmentDocCreateRequest;

    switch (this.dialogMode) {
      case 'CREATE':
        this.store.dispatch(CovidAssessmentActions.createCovidAssessment({
          request: request
        }));
        break;
      case 'DUPLICATE':
        this.store.dispatch(CovidAssessmentActions.duplicateCovidAssessment({
          id: this.duplicateCovidAssessmentPayload?.payload?.docId,
          request: request
        }));
        break;
    }
  }

}
